import { useEffect, useRef, useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { getUserFromClerkId, createUserFromClerkId } from '@/hooks/useUser';
import { createFileRoute } from '@tanstack/react-router';
import { BrandedSpinner } from '@/components/BrandedSpinner';
import bg2 from '@/assets/bg8.webp';
import { userHasPaidAccess } from '@/lib/userSubscriptionHelpers';
import { getSafeRedirectPath } from '@/lib/authHelpers';

const TIMEOUT_MS = 15_000;

type CallbackSearch = { invitationcode?: string; preview?: string; redirectTo?: string }

export const Route = createFileRoute('/signing-in')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    invitationcode: typeof search.invitationcode === 'string' ? search.invitationcode : undefined,
    preview: typeof search.preview === 'string' ? search.preview : undefined,
    redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
  }),
  component: RegisterSsoCallbackComponent,
  errorComponent: () => null,
});

function RegisterSsoCallbackComponent() {
  const { handleRedirectCallback } = useClerk();
  const { user, isLoaded, isSignedIn } = useUser();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const invitationcode = search.invitationcode;
  const safeRedirectTo = getSafeRedirectPath(search.redirectTo);
  const isPreview = !!search.preview;

  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const mountedRef = useRef(true);
  const [dotCount, setDotCount] = useState(0);

  const { data: userData, isLoading: userLoading, isError: userQueryError } = getUserFromClerkId(user?.id ?? '', { enabled: !!user && !isPreview });
  const createUserMutation = createUserFromClerkId();
  const callbackHandledRef = useRef(false);
  const createAttemptedRef = useRef(false);

  // Global timeout — if we haven't navigated away within TIMEOUT_MS, show error
  useEffect(() => {
    if (isPreview) return;
    const timer = window.setTimeout(() => {
      if (mountedRef.current) {
        setStatus('error');
        setErrorMessage("This is taking longer than expected. Please try signing in again.");
      }
    }, TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPreview]);

  // Handle OAuth redirect callback
  useEffect(() => {
    if (isPreview) return;
    if (callbackHandledRef.current) return;
    if (!isLoaded) return;
    if (user) return;

    const hasCallbackData =
      window.location.hash.length > 1 ||
      window.location.href.includes('__clerk_status') ||
      window.location.href.includes('__clerk_created_session_id');

    if (!hasCallbackData) return;

    callbackHandledRef.current = true;
    const currentSearch = window.location.search;
    handleRedirectCallback({
      signUpForceRedirectUrl: `/signing-in${currentSearch}`,
      signInForceRedirectUrl: `/signing-in${currentSearch}`,
    }).catch((err: unknown) => {
      console.error('SSO callback handling failed:', err);
      if (mountedRef.current) {
        setStatus('error');
        setErrorMessage("We couldn't complete sign-in with your provider. Please try again.");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, user, isPreview]);

  // Subtle animated ellipsis
  useEffect(() => {
    if (!mountedRef.current) return;
    let dotTimeout: number | undefined;
    const tick = () => {
      setDotCount((prev) => (prev + 1) % 4);
      dotTimeout = window.setTimeout(tick, 350);
    };
    tick();
    return () => {
      if (dotTimeout) clearTimeout(dotTimeout);
    };
  }, []);

  // Handle user query error (e.g. user not found in our DB, Zod validation, API error)
  useEffect(() => {
    if (isPreview || !isLoaded || !isSignedIn || !user) return;
    if (userLoading) return;

    // Query errored — surface it instead of silently falling through
    if (userQueryError && !createAttemptedRef.current) {
      // The user might not exist in our DB yet (e.g. first SSO sign-in).
      // Try creating them once before showing an error.
      createAttemptedRef.current = true;
      createUserMutation.mutate(
        { clerkId: user.id },
        {
          onSuccess: () => {
            if (invitationcode) {
              navigate({ to: '/invite', search: { invitationcode, __clerk_ticket: undefined } });
            } else {
              window.location.assign(safeRedirectTo ?? '/subscribe');
            }
          },
          onError: (error) => {
            console.error('User creation failed:', error);
            if (mountedRef.current) {
              setStatus('error');
              setErrorMessage("We had trouble setting up your account. Please try signing in again.");
            }
          },
        }
      );
      return;
    }
  }, [isPreview, isLoaded, isSignedIn, user, userLoading, userQueryError]);

  // Main routing — only runs when we have user data successfully
  useEffect(() => {
    if (isPreview) return;
    if (!isLoaded || !isSignedIn || userLoading) return;
    if (!user) return;
    if (userQueryError) return; // handled by the effect above

    if (userData) {
      const hasPaidAccess = userHasPaidAccess(userData as any);
      if (hasPaidAccess) {
        navigate({ to: '/', search: { invitationcode: undefined, __clerk_ticket: undefined } });
        return;
      }
      if (invitationcode) {
        navigate({ to: '/invite', search: { invitationcode, __clerk_ticket: undefined } });
      } else {
        window.location.assign(safeRedirectTo ?? '/subscribe');
      }
      return;
    }

    // userData is undefined but query didn't error — new user, create once
    if (!createAttemptedRef.current) {
      createAttemptedRef.current = true;
      createUserMutation.mutate(
        { clerkId: user.id },
        {
          onSuccess: () => {
            if (invitationcode) {
              navigate({ to: '/invite', search: { invitationcode, __clerk_ticket: undefined } });
            } else {
              window.location.assign(safeRedirectTo ?? '/subscribe');
            }
          },
          onError: (error) => {
            console.error('User creation failed:', error);
            if (mountedRef.current) {
              setStatus('error');
              setErrorMessage("We had trouble setting up your account. Please try signing in again.");
            }
          },
        }
      );
    }
  }, [
    isLoaded,
    isSignedIn,
    user,
    userData,
    userLoading,
    userQueryError,
    navigate,
    invitationcode,
    isPreview,
    safeRedirectTo
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleRetry = () => {
    const retrySearch = new URLSearchParams();
    if (invitationcode) retrySearch.set('invitationcode', invitationcode);
    if (safeRedirectTo) retrySearch.set('redirectTo', safeRedirectTo);
    const retryUrl = `/signin${retrySearch.toString() ? `?${retrySearch.toString()}` : ''}`;
    window.location.assign(retryUrl);
  };

  if (status === 'error') {
    return (
      <div style={{ backgroundImage: `url(${bg2})` }} className="flex flex-col items-center justify-center min-h-screen py-32 px-4 bg-white">
        <div className="bg-white rounded-lg shadow-lg p-10 text-center max-w-sm mx-auto">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
              <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-base text-gray-500 mb-6">{errorMessage}</p>
          <button
            onClick={handleRetry}
            className="w-full py-3 px-4 text-base font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors cursor-pointer"
          >
            Try signing in again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundImage: `url(${bg2})` }} className="flex flex-col items-center justify-center min-h-screen py-32 px-4 bg-white">
      <div className="bg-white rounded-lg shadow-lg p-10 text-center max-w-sm mx-auto">
        <div className="flex justify-center mb-6">
          <BrandedSpinner size="xl" />
        </div>
        <div className="flex items-center justify-center gap-3 mb-2">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Signing you in</h2>
        </div>
        <p className="text-lg text-gray-500">
          <span aria-live="polite">Just a moment</span>
          <span
            aria-hidden="true"
            className="inline-block text-gray-500 text-left whitespace-pre w-[3ch] text-base leading-none"
          >
            {'.'.repeat(dotCount).padEnd(3, ' ')}
          </span>
        </p>
      </div>
    </div>
  );
}
